/**
 * SB5-B.2 — ContractPositionStatusChip — Apple-palette band mapping.
 *
 * Per `03-page-contract-position.md` §Table column 5:
 *   white  ← CREATED, SCHEDULED
 *   yellow ← EXECUTION
 *   green  ← COMPLETED
 *   red    ← WITHDRAWN, REJECTED
 *
 * Mirrors `ContractStatusChip` shape — `data-state` + `data-color`
 * test hooks expose the band without scraping CSS.
 */
import { type ReactElement } from "react";
import { Chip } from "@mui/material";
import type { ContractPositionState } from "./types.js";
import { CONTRACT_POSITION_STATE_COLOR } from "./contractPositionColors.js";

// The state → colour band map is shared with the project map markers (FE-1) so
// both surfaces use ONE palette — see `contractPositionColors.ts`.
const STATE_COLOR = CONTRACT_POSITION_STATE_COLOR;

const STATE_LABEL: Record<ContractPositionState, string> = {
  CREATED: "Created",
  SCHEDULED: "Scheduled",
  EXECUTION: "Execution",
  COMPLETED: "Completed",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
};

export function contractPositionStateLabel(
  state: ContractPositionState,
): string {
  return STATE_LABEL[state];
}

export function ContractPositionStatusChip({
  state,
}: {
  state: ContractPositionState;
}): ReactElement {
  const color = STATE_COLOR[state];
  return (
    <Chip
      size="small"
      label={STATE_LABEL[state]}
      data-state={state}
      data-color={color}
      sx={(t) => {
        const isDark = t.palette.mode === "dark";
        switch (color) {
          case "white":
            return {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.12)"
                : "rgba(0,0,0,0.04)",
              color: t.palette.text.primary,
              border: `1px solid ${t.palette.divider}`,
            };
          case "yellow":
            return {
              backgroundColor: isDark
                ? "rgba(255, 159, 10, 0.22)"
                : "rgba(255, 149, 0, 0.14)",
              color: isDark ? "#FFD681" : "#8C5A00",
            };
          case "green":
            return {
              backgroundColor: isDark
                ? "rgba(48, 209, 88, 0.22)"
                : "rgba(52, 199, 89, 0.14)",
              color: isDark ? "#7DDFA0" : "#0F6E3F",
            };
          case "red":
            return {
              backgroundColor: isDark
                ? "rgba(255, 69, 58, 0.22)"
                : "rgba(255, 59, 48, 0.14)",
              color: isDark ? "#FF8A80" : "#B3261E",
            };
        }
      }}
    />
  );
}
